/**
 * Sonner Adapter for the design system
 * 
 * This component adapts the Sonner toast component to fit our premium design system.
 */
import * as React from "react"
import { Toaster as SonnerBase } from "../sonner"
import { toast as sonnerToast } from "sonner"
import { cn } from "@/lib/utils"
import { SonnerBaseProps, ToastOptions, ToastVariant } from "@repo/design-system/types"

/**
 * Sonner adapter props
 * Only extends the design system base props as specified in the adapter pattern
 */
export interface SonnerProps extends SonnerBaseProps {}

/**
 * Maps our design system toast options to Sonner toast options
 */
function mapToastOptions(options?: ToastOptions): any {
  if (!options) return undefined;
  
  return {
    description: options.description,
    duration: options.duration,
    icon: options.icon,
    action: options.action,
    dismissible: options.dismissible,
    ...(options.variant && { className: getVariantClass(options.variant) })
  };
}

/**
 * Gets the CSS class for a toast variant
 */
function getVariantClass(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return "bg-success/15 border-success text-success-foreground";
    case 'error':
      return "bg-error/15 border-error text-error-foreground";
    case 'warning':
      return "bg-warning/15 border-warning text-warning-foreground";
    case 'info':
      return "bg-primary/15 border-primary text-primary-foreground";
    default:
      return "bg-background border-border text-foreground";
  }
}

/**
 * Maps design system props to base component props
 */
function mapToBaseProps(props: SonnerProps): React.ComponentProps<typeof SonnerBase> {
  const { 
    premium,
    position,
    theme,
    richColors,
    expand,
    offset,
    gap,
    closeButton,
    ...restProps
  } = props;
  
  // Return the mapped props for the base component
  return {
    position,
    theme: theme as any,
    richColors,
    expand,
    offset: offset as number | undefined,
    gap: gap as number | undefined,
    closeButton,
    ...restProps
  };
}

/**
 * Sonner adapter component
 * Adapts the sonner Toaster component to use our design system
 */
export const Sonner = React.forwardRef<
  React.ElementRef<typeof SonnerBase>,
  SonnerProps
>((props, ref) => {
  const { premium = false, ...designSystemProps } = props;
  
  // Map our design system props to base component props
  const baseProps = mapToBaseProps(designSystemProps);
  
  return (
    <SonnerBase
      ref={ref}
      className={cn(
        "sonner-adapter",
        premium && "premium-sonner",
        baseProps.className
      )}
      toastOptions={{
        classNames: {
          toast: cn(
            "premium-radius-xl border-2",
            premium && "premium-shadow"
          ),
          actionButton: cn(
            premium && "premium-transition-all premium-hover-lift premium-radius-full"
          ),
          cancelButton: cn(
            premium && "premium-radius-full"
          ),
          success: "bg-success/15 border-success text-success-foreground",
          error: "bg-error/15 border-error text-error-foreground",
          warning: "bg-warning/15 border-warning text-warning-foreground",
          info: "bg-primary/15 border-primary text-primary-foreground",
        },
        ...baseProps.toastOptions,
      }}
      {...baseProps}
    />
  );
});

Sonner.displayName = "Sonner";

/**
 * Enhanced toast function that implements our design system
 */
export const toast = Object.assign(
  (message: string, options?: ToastOptions) => {
    return sonnerToast(message, mapToastOptions(options));
  },
  {
    success: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      return sonnerToast.success(message, mapToastOptions({ ...options, variant: 'success' }));
    },
    error: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      return sonnerToast.error(message, mapToastOptions({ ...options, variant: 'error' }));
    },
    warning: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      return sonnerToast.warning(message, mapToastOptions({ ...options, variant: 'warning' }));
    },
    info: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      return sonnerToast(message, mapToastOptions({ ...options, variant: 'info' }));
    },
    promise: (
      promise: Promise<any>,
      options: {
        loading: string;
        success: string | ((data: any) => string);
        error: string | ((error: unknown) => string);
      },
      toastOptions?: Omit<ToastOptions, 'variant'>
    ) => {
      // Fix: Sonner expects the options and toast options to be a single object
      return sonnerToast.promise(promise, {
        ...options,
        ...mapToastOptions(toastOptions)
      });
    },
    dismiss: (toastId?: string | number) => {
      return sonnerToast.dismiss(toastId);
    }
  }
);
